// The Portal wordmark: custom geometry, not a typeface. Six letters on a 120-unit
// cap height, stroked 28 units with butt caps and miter joins. Reproduced verbatim
// from the handoff (docs/plans/2026-09-portal-logo/final/) — do not re-draw,
// re-space or substitute a font. The same numbers live server-side in
// PartyResume.Api/Services/PortalWordmark.cs; change one, change both.
//
// On the site the logo is always the primary lockup: the spinning portal IS the O.
// 750 × 122 units, minimum cap 24px. Below that, PortalMark — the portal alone.
// (The secondary — portal beside the word — survives only server-side, for the
// wallet pass, which is too small for the primary.)

export const CAP = 120;
// The R's diagonal leg overshoots the baseline by ~1.7 units, so every part sits in
// a 122-tall box and they align by sharing that height.
export const BOX_H = 122;
export const WORD_W = 750;

export const STROKE_ATTRS = 'fill="none" stroke-width="28" stroke-linecap="butt" stroke-linejoin="miter"';

export const LETTERS = {
  P: 'M14,0V120M14,14H53A29,29 0 0 1 53,72H14',
  R: 'M14,0V120M14,14H53A29,29 0 0 1 53,72H14M46,72L84,112',
  T: 'M0,14H96M48,14V120',
  A: 'M14,120V48A34,34 0 0 1 82,48V120M14,88H82',
  L: 'M14,0V106H82',
};
// Letter x-positions in the full word.
export const X = { P: 0, O: 126, R: 276, T: 402, A: 528, L: 654 };

// Primary lockup, as % of the 750-wide box. The O slot is 120 units square — the
// cap exactly — at x 126, and the portal is centred on it.
export const PRIMARY = {
  p: { left: 0, width: 12.8 },
  o: { left: 16.8, width: 16, height: 98.361 },
  rtal: { left: 36.8, width: 63.2 },
};

// The portal art (portal-o.png / .webp, 1024², transparent): its bright inner ring
// spans ~285 of a 512 canvas, so the image is drawn at 512/285 × the O slot, centred
// on the slot. That makes the ring match the cap height and runs the swirl's tails
// behind the P and the R. The art renders UNDER the letters and is never recoloured.
export const PORTAL_ART_SCALE = 512 / 285;

// The same art for markup that can't import from the bundle (the landing and New
// Here string lockups). Under public/, so the URL is stable.
export const PORTAL_ART_SRC = '/brand/portal-o.webp';

// PortalMark draws the art overhanging its box by this much per side, so the ring
// — not the faint tails — fills the box.
export const MARK_OVERHANG = 0.12;

// ── The trace (docs/plans/2026-09-portal-logo/final/README.md § Motion) ─────
// Portal party colours: the Share Sheet border / Portal swirl palette.
export const PARTY_COLORS = ['#FD199F', '#FF33C6', '#B5B1FF', '#127BFD', '#22ECFB', '#FAE1B4', '#FCAA6D'];

// Timing, as fractions of one cycle. A streak of Portal light runs P → O → R → T →
// A → L in reading order, the portal swells as the streak passes it, then the word
// rests on plain ink for the back half of the loop.
export const TRACE = {
  cycleMs: 4800,   // one full loop at speed 1
  sweep: 0.16,     // how long the streak takes to cross one letter
  step: 0.11,      // stagger between slots (P=0, portal=1, R=2, T=3, A=4, L=5)
  pulse: 0.2,      // the portal's swell, scale 1 → 1.08 → 1, starting at slot 1
  dashLen: 34,     // streak length, % of each letter's path (pathLength=100)
};

// The gradient is fixed and spans 375 units, repeating, so each letter's streak picks
// up different colours from its position in the word.
export const GRADIENT_SPAN = 375;

let gradSeq = 0;
const gradientSvg = (id, x2 = GRADIENT_SPAN, units = 'userSpaceOnUse') =>
  `<linearGradient id="${id}" gradientUnits="${units}" x1="0" y1="0" x2="${x2}" y2="${units === 'userSpaceOnUse' ? 0 : 1}" spreadMethod="repeat">`
  + [...PARTY_COLORS, PARTY_COLORS[0]].map((c, i) => `<stop offset="${i / PARTY_COLORS.length}" stop-color="${c}"/>`).join('')
  + '</linearGradient>';

// P, (portal = slot 1), R, T, A, L — reading order drives the trace's stagger.
const WORD = [['P', 0], ['R', 2], ['T', 3], ['A', 4], ['L', 5]];
const letterPaths = (extra = () => '') =>
  WORD.map(([k, slot]) => `<path d="${LETTERS[k]}" transform="translate(${X[k]},0)"${extra(slot)}/>`).join('');

/**
 * Primary lockup as markup, for the pages that are exported HTML strings rather
 * than React (landing, New Here) — the same DOM PortalLogo renders: the spinning
 * portal in the O slot (under everything), the ink word, and the trace copy of it
 * stroked in the party gradient (invisible until a pass runs it —
 * utils/portalLogoMotion starts the scheduler). `width` is any CSS length — the box
 * keeps the 750:122 ratio, so `clamp()` and `vw` work. The box is overflow: visible
 * and the tails reach ~0.4 × cap above and below it; give the parent that room.
 */
export function primaryLockupHtml({ width } = {}) {
  const gid = `pw-grad-${(gradSeq += 1)}`;
  const o = PRIMARY.o;
  const inset = `${(-(PORTAL_ART_SCALE - 1) / 2) * 100}%`;
  return `<span role="img" aria-label="Portal" data-portal-logo style="position:relative;display:block;width:${width};aspect-ratio:${WORD_W}/${BOX_H};">`
    + `<span aria-hidden="true" style="position:absolute;left:${o.left}%;top:0;width:${o.width}%;height:${o.height}%;">`
    + `<span data-pulse style="position:absolute;inset:${inset};display:block;">`
    + `<img data-portal-o class="pr-portal-spin" src="${PORTAL_ART_SRC}" alt="" draggable="false" style="display:block;width:100%;height:100%;">`
    + '</span></span>'
    + `<svg aria-hidden="true" viewBox="0 0 ${WORD_W} ${BOX_H}" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;">`
    + `<defs>${gradientSvg(gid)}</defs>`
    + `<g stroke="currentColor" ${STROKE_ATTRS}>${letterPaths()}</g>`
    + `<g stroke="url(#${gid})" ${STROKE_ATTRS}>${letterPaths((slot) => ` pathLength="100" stroke-dasharray="${TRACE.dashLen} 200" stroke-dashoffset="${TRACE.dashLen}" data-trace="${slot}"`)}</g>`
    + '</svg></span>';
}

/** The portal's spin, for a document that doesn't load index.css (the New Here iframe). */
export const PORTAL_SPIN_CSS = '@keyframes prPortalSpin{to{transform:rotate(360deg)}}'
  + '.pr-portal-spin{animation:prPortalSpin 10s linear infinite;will-change:transform}'
  + '@media (prefers-reduced-motion:reduce){.pr-portal-spin{animation:none}}';
